import type { FC } from "react";
import { useState } from "react";
import { Button, Dropdown, TextInput } from "flowbite-react";
import { HiCalendar } from "react-icons/hi";
import {
  formatDateInputValue,
  isValidDate,
  parseDateInputValue,
} from "../utils/dateInput";
import "./date-range-picker.css"; // Import the CSS file

interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

interface DateRangePickerProps {
  onChange: (range: DateRange) => void;
  className?: string;
  label?: string;
}

const DateRangePicker: FC<DateRangePickerProps> = ({
  onChange,
  className,
  label = "Zeitraum",
}) => {
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
  });

  const formatDate = (date: Date | null) => {
    if (!isValidDate(date)) return "";
    return date.toLocaleDateString("de-DE");
  };

  const handleReset = () => {
    const newRange = { startDate: null, endDate: null };
    setDateRange(newRange);
    onChange(newRange);
  };

  const handlePresetSelect = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);

    const newRange = { startDate: start, endDate: end };
    setDateRange(newRange);
    onChange(newRange);
  };

  const handleDateChange = (type: "start" | "end", value: string) => {
    const date = parseDateInputValue(value);
    const newRange = {
      ...dateRange,
      [type === "start" ? "startDate" : "endDate"]: date,
    };
    setDateRange(newRange);
    onChange(newRange);
  };

  const displayText =
    isValidDate(dateRange.startDate) && isValidDate(dateRange.endDate)
      ? `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`
      : "Zeitraum";

  return (
    <div className={`w-full ${className || ""}`}>
      {label && (
        <div className="mb-1 block text-sm font-medium text-gray-900 dark:text-white">
          {label}
        </div>
      )}
      <Dropdown
        label={
          <div className="flex items-center gap-2">
            <HiCalendar className="h-4 w-4" />
            <div className="max-w-[120px] truncate sm:max-w-none">
              {displayText}
            </div>
          </div>
        }
        dismissOnClick={false}
        placement="bottom"
        className={`date-range-picker-dropdown w-full ${className || ""}`}
      >
        <div className="w-[340px] max-w-[calc(100vw-20px)] p-4">
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="start-date"
                className="mb-1 block text-sm font-medium text-gray-900 dark:text-white"
              >
                Von
              </label>
              <TextInput
                id="start-date"
                type="date"
                value={formatDateInputValue(dateRange.startDate)}
                onChange={(e) => handleDateChange("start", e.target.value)}
                className="sentry-unmask"
              />
            </div>
            <div>
              <label
                htmlFor="end-date"
                className="mb-1 block text-sm font-medium text-gray-900 dark:text-white"
              >
                Bis
              </label>
              <TextInput
                id="end-date"
                type="date"
                value={formatDateInputValue(dateRange.endDate)}
                onChange={(e) => handleDateChange("end", e.target.value)}
                className="sentry-unmask"
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Button size="sm" onClick={() => handlePresetSelect(7)}>
              Letzte 7 Tage
            </Button>
            <Button size="sm" onClick={() => handlePresetSelect(30)}>
              Letzte 30 Tage
            </Button>
            <Button size="sm" onClick={() => handlePresetSelect(90)}>
              Letzte 90 Tage
            </Button>
            <div className="my-2 border-t border-gray-200 dark:border-gray-700" />
            <Button size="sm" color="gray" onClick={handleReset}>
              Zurücksetzen
            </Button>
          </div>
        </div>
      </Dropdown>
    </div>
  );
};

export default DateRangePicker;
